function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '-';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function statusBadgeClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (['success', 'completed'].includes(normalized)) return 'bg-success';
  if (['failed', 'error', 'rejected'].includes(normalized)) return 'bg-danger';
  if (['queued', 'pending'].includes(normalized)) return 'bg-warning text-dark';
  if (['running', 'processing'].includes(normalized)) return 'bg-info text-dark';
  return 'bg-secondary';
}

function humanizeToken(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  return normalized
    .replace(/tenant[-_.\s]+db/gi, 'database')
    .replace(/tenant[-_.\s]+object/gi, 'object storage')
    .replace(/\btenant\b/gi, 'system')
    .toLowerCase()
    .split(/[._-\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function formatBackupTimestamp(filename) {
  const match = String(filename || '').match(/(\d{8})T(\d{6})Z/);
  if (!match) {
    return '';
  }

  const [, datePart, timePart] = match;
  return `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)} ${timePart.slice(0, 2)}:${timePart.slice(2, 4)} UTC`;
}

function formatBackupDisplayName(objectKey, kind = 'scheduled') {
  const filename = String(objectKey || '').split('/').pop() || '';
  const timestamp = formatBackupTimestamp(filename);
  const prefix = kind === 'pre_restore' ? 'Safety backup' : 'Database backup';

  return timestamp ? `${prefix} ${timestamp}` : prefix;
}

function formatDownloadFilename(objectKey, kind = 'scheduled') {
  const filename = String(objectKey || '').split('/').pop() || '';
  const timestamp = filename.match(/(\d{8}T\d{6}Z)/)?.[1];
  const prefix = kind === 'pre_restore' ? 'safety-backup' : 'database-backup';

  return `${prefix}${timestamp ? `-${timestamp}` : ''}.sql.gz`;
}

function formatFilesDownloadFilename(objectKey) {
  const filename = String(objectKey || '').split('/').pop() || '';
  const timestamp = filename.match(/(\d{8}T\d{6}Z)/)?.[1];

  return `backup-files${timestamp ? `-${timestamp}` : ''}.zip`;
}

export function inferDbNameFromObjectKey(objectKey) {
  const filename = String(objectKey || '').split('/').pop() || '';
  const match = filename.match(/^(.*)-\d{8}T\d{6}Z\.sql\.gz$/);

  return match?.[1] || '';
}

export function buildTenantBackupObject(item) {
  const objectKey = String(item?.objectKey || '');
  const kind = ['pre_restore', 'backup_set'].includes(item?.kind) ? item.kind : 'scheduled';
  const downloadSizeBytes = item?.dbSizeBytes ?? item?.sizeBytes;
  const objectCount = Number(item?.objectCount || 0);

  return {
    objectKey,
    filename: objectKey.split('/').pop() || objectKey,
    displayName: formatBackupDisplayName(objectKey, kind),
    downloadFilename: formatDownloadFilename(objectKey, kind),
    filesDownloadFilename: formatFilesDownloadFilename(objectKey),
    sizeLabel: formatBytes(downloadSizeBytes),
    lastModifiedLabel: formatDateTime(item?.lastModified),
    kind,
    backupSetId: item?.backupSetId || '',
    dbObjectKey: item?.dbObjectKey || objectKey,
    objectCount,
    canDownloadFiles: kind === 'backup_set' && objectCount > 0 && Boolean(item?.backupSetId),
    inferredDbName: inferDbNameFromObjectKey(objectKey),
  };
}

export function buildTenantBackupRun(item) {
  const objectKey = item?.objectKey || item?.backupObjectKey || '';
  const preRestoreBackupObjectKey = item?.preRestoreBackupObjectKey || '';

  return {
    runId: String(item?.runId || ''),
    operation: String(item?.operation || ''),
    operationLabel: humanizeToken(item?.operation),
    status: String(item?.status || 'unknown'),
    statusBadgeClass: statusBadgeClass(item?.status),
    requestedAtLabel: formatDateTime(item?.requestedAt),
    finishedAtLabel: formatDateTime(item?.finishedAt),
    requestedByLabel: item?.requestedByEmail || '-',
    objectKey,
    objectLabel: objectKey ? formatBackupDisplayName(objectKey) : '-',
    preRestoreBackupObjectKey,
    preRestoreBackupLabel: preRestoreBackupObjectKey ? formatBackupDisplayName(preRestoreBackupObjectKey, 'pre_restore') : '',
    errorLabel: item?.error || '-',
  };
}

export function buildTenantBackupObjects(response) {
  return (response?.backups || []).map(buildTenantBackupObject);
}

export function buildTenantBackupRuns(response) {
  return (response?.runs || []).map(buildTenantBackupRun);
}
